import type { Channel } from '@/bindings/Channel'
import { Button } from '@/components/button'
import type { CodeStatus } from '@/features/auth/code-status'
import { useT } from '@/lib/i18n/translate'
import { formatCountdown, useNow } from '@/lib/time'

export interface CodeStatusPanelProps {
  status: CodeStatus | null
  channel: Channel
  /** Forgot-password copy hedges ("if it matches an account"); register/profile does not. */
  flow: 'verify' | 'reset'
  /** Whether the picked country gets SMS at all (drives the unsupported hint). */
  smsCovered: boolean
  /** The last verify failed on the code: an operator's text may be spent or expired. */
  codeRejected: boolean
  onResend: () => void
  resendPending: boolean
  /** Back to the request step with the email channel; absent where switching makes no sense. */
  onUseEmail?: (() => void) | undefined
}

/**
 * The verify step's two lines (I1): what happened to the code (with a
 * countdown for automated ones) and what the next request will do — never
 * a silent resend that quietly lands in the manual queue.
 */
export function CodeStatusPanel({
  status,
  channel,
  flow,
  smsCovered,
  codeRejected,
  onResend,
  resendPending,
  onUseEmail,
}: CodeStatusPanelProps) {
  const t = useT()
  const response = status?.response ?? null
  const expiresAt =
    status && response?.expires_minutes != null ? status.requestedAt + response.expires_minutes * 60_000 : null
  const now = useNow(expiresAt !== null)
  const phone = channel === 'phone'
  const reset = flow === 'reset'

  const manualNotice = t(reset ? 'auth.resetCodeSentPhone' : 'auth.codeSentPhone')
  const unsupportedHint = phone && !smsCovered ? t('auth.smsUnsupportedHint') : null

  const resendButton = (label: 'auth.resendCode' | 'auth.askOperator') => (
    <Button type="button" variant="secondary" onClick={onResend} loading={resendPending} block>
      {t(label)}
    </Button>
  )
  const useEmailButton =
    phone && onUseEmail ? (
      <Button type="button" variant="ghost" onClick={onUseEmail} block>
        {t('auth.useEmailInstead')}
      </Button>
    ) : null

  // No recorded request (a code from another device, or a refresh that lost
  // the tab): the generic copy, and a plain resend.
  if (!response) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-muted text-sm">
          {phone ? manualNotice : t(reset ? 'auth.resetCodeSentEmail' : 'auth.codeSentEmail')}
        </p>
        {unsupportedHint && <p className="text-muted text-sm">{unsupportedHint}</p>}
        {resendButton('auth.resendCode')}
        {useEmailButton}
      </div>
    )
  }

  const left = response.requests_left
  let statusLine: string
  if (response.delivery === 'manual') {
    statusLine = manualNotice
  } else {
    const sentTo = t(
      response.delivery === 'sms'
        ? reset
          ? 'auth.resetCodeSentToPhone'
          : 'auth.codeSentToPhone'
        : reset
          ? 'auth.resetCodeSentToEmail'
          : 'auth.codeSentToEmail',
    )
    const clock =
      expiresAt === null ? '' : expiresAt > now ? t('auth.expiresIn', { time: formatCountdown(expiresAt - now) }) : t('auth.codeExpired')
    statusLine = `${sentTo} ${clock}`.trim()
  }

  let actionLine
  if (left === 0) {
    actionLine = (
      <>
        <p className="text-muted text-sm">{t('auth.noMoreCodes')}</p>
        {useEmailButton}
      </>
    )
  } else if (response.delivery === 'manual') {
    // No plain resend while the operator's text stands; after a rejected
    // code (5 tries spent, or a day old) the queue must be reachable again.
    actionLine = (
      <>
        {codeRejected && resendButton('auth.resendCode')}
        {useEmailButton}
      </>
    )
  } else if (response.delivery === 'sms' && left === 1) {
    // The next request is the last of the day and goes to an operator: say so first.
    actionLine = (
      <>
        <p className="text-muted text-sm">{t('auth.smsLimitNotice')}</p>
        {resendButton('auth.askOperator')}
        {useEmailButton}
      </>
    )
  } else {
    actionLine = (
      <>
        <p className="text-muted text-sm">{t('auth.didntGetIt')}</p>
        {resendButton('auth.resendCode')}
        {useEmailButton}
      </>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <p role="status" className="text-muted text-sm">
        {statusLine}
      </p>
      {unsupportedHint && <p className="text-muted text-sm">{unsupportedHint}</p>}
      {actionLine}
    </div>
  )
}
